import React, { useCallback, useEffect, useReducer, useState } from "react";
import { Persona } from "@soulmachines/smwebsdk";
import { isMobile } from "react-device-detect";
import { UserAction, InitalStateProps, UserActionType } from "../types/Store";
import {
  HeadlineTypes,
  PlaintextTypes,
  CharacterSelectionTypes,
  ImgCtaButtonsTypes,
  BulletListTypes,
  TipsTypes,
  SliderTypes,
  ImgTypes,
  ImgDescTypes,
  RedirectTypes,
} from "../types/ContentCard";
import { createErrorObject } from "../utils/createErrorObject";

// Setting context variables
export const Store = React.createContext(
  {} as {
    state: InitalStateProps;
    dispatch: Function;
  }
);
// Inital State for Context
const initialState: InitalStateProps = {
  connected: false,
  error: false,
  errorType: null,
  // create a new scene object
  scene: null,
  persona: null,
  videoPlaying: false,
  videoHeight: window.innerHeight,
  videoWidth: window.innerWidth,
  mute: true,
  pause: false,
  transcript: [],
  shownCardsThisTurn: [],
  contentCardThisturn: [],
  contentCards: {},
  // NLP gives us results as it processes final user utterance
  intermediateUserUtterance: "",
  tabletLandscape: false,
  conversationTranscript: false,
  skipClicked: false,
};

// Reducer that can be accessed through dispatch
const reducer = (state: InitalStateProps, action: UserAction) => {
  switch (action.type) {
    case "UPDATE_SCENE":
      const { scene } = action.payload;
      return {
        ...state,
        scene,
      };

    case "CONNECT_SCENE":
      // calc resolution w/ device pixel ratio
      const deviceHeight = Math.round(
        state.videoHeight * window.devicePixelRatio
      );
      const deviceWidth = Math.round(
        state.videoWidth * window.devicePixelRatio
      );
      state.scene?.sendVideoBounds(deviceWidth, deviceHeight);

      // we can't disable logging until after the connection is established
      // logging is pretty crowded, not recommended to enable
      // unless you need to debug emotional data from webcam
      state.scene?.session()?.setLogging(false);
      // We turn off speech until we recieve a message so the DP doesn't falling into faillback loop
      state.scene?.stopRecognize();

      // We create new Persona when we connect Scene
      const persona = new Persona(state.scene!, "1");

      if (!isMobile || state.tabletLandscape) {
        persona.animateToNamedCameraWithOrbitPan(
          "__start",
          1,
          0,
          0,
          -4, // X Pan
          1 // Y Tilt
        );
      }

      return { ...state, connected: true, persona };
    case "START_VIDEO":
      return { ...state, videoPlaying: true };
    case "TOGGLE_MUTE":
      return {
        ...state,
        mute: !state.mute,
        intermediateUserUtterance: "",
        ...(state.mute === false && { conversationTranscript: true }),
      };
    case "SET_VIDEO_DIMENSIONS":
      // We need to re-run calcs before sending VideoBounds
      const { videoHeight, videoWidth } = action.payload;
      const newHeight = Math.round(videoHeight * window.devicePixelRatio);
      const newWidth = Math.round(videoWidth * window.devicePixelRatio);
      state.scene?.sendVideoBounds(newWidth, newHeight);
      // We animate dp to move to left
      if (!isMobile || state.tabletLandscape) {
        state.persona?.animateToNamedCameraWithOrbitPan(
          "__start",
          1,
          0,
          0,
          -5, // X Pan
          0 // Y Tilt
        );
      } else {
        // This else exist for tablet rotation
        state.persona?.animateToNamedCameraWithOrbitPan(
          "__start",
          1,
          0,
          0,
          0, // X Pan
          0 // Y Tilt
        );
      }

      return {
        ...state,
        videoHeight: action.payload.videoHeight,
        videoWidth: action.payload.videoWidth,
      };
    case "TOGGLE_PAUSE":
      return {
        ...state,
        pause: !state.pause,
      };
    case "ADD_CONVERSATION_MESSAGE":
      const { payload } = action;
      const { source, text } = payload;
      const content =
        typeof text === "object" && text !== null
          ? state.contentCards[text.id]
          : {
              component: `${source}Transcript`,
              data: {
                transcript: text,
              },
            };
      // In case there is a invalid content card
      if (!content)
        return {
          ...state,
        };

      if (content.component === "redirect") {
        const redirectReassign = content as RedirectTypes;
        window.open(redirectReassign.data.url, "_blank");
        return {
          ...state,
        };
      }
      let newEntry = {
        ...(typeof text === "object" &&
          text !== null && {
            cardId: text.id,
          }),
        source,
        timestamp: new Date().toISOString(),
        content,
      };
      // Once the dp responds we turn back on speech ability
      state.transcript.length === 0 && state.scene?.startRecognize();
      return {
        ...state,
        ...(state.transcript.length === 0 && { mute: false }),

        transcript: [...state.transcript, { ...newEntry }],
        // Need to keep track of shown card in case skip button is clicked
        ...(typeof text === "object" &&
          text !== null && {
            shownCardsThisTurn: [...state.shownCardsThisTurn, { ...newEntry }],
          }),
        intermediateUserUtterance: "",
      };
    case "SET_INTERMEDIATE_USER_UTTERANCE":
      return {
        ...state,
        intermediateUserUtterance: action.payload.text,
      };
    case "SET_ERROR":
      const { errorType } = action.payload;

      return {
        ...state,
        error: true,
        loading: false,
        // We generate an Object in order to allow dynamic text in Error Modal
        errorType: createErrorObject(errorType),
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: false,
        errorType: null,
      };

    case "SET_TABLET_LANDSCAPE":
      return {
        ...state,
        tabletLandscape: action.payload.tabletLandscape,
      };

    case "MODIFY_CONTENT_CARDS":
      return {
        ...state,
        contentCards: { ...state.contentCards, ...action.payload.contentCards },
      };
    case "TOGGLE_CONVERSATION_TRANSCRIPT":
      return {
        ...state,
        conversationTranscript: !state.conversationTranscript,
      };
    case "SKIP": {
      if (state.skipClicked || state.contentCardThisturn.length === 0)
        return { ...state };
      state.persona?.stopSpeaking();
      const tempArray: any = [];
      state.contentCardThisturn.map((cardName) => {
        if (
          !state.shownCardsThisTurn.filter(
            (card) => card.cardId === cardName
          )[0]
        ) {
          tempArray.push({
            cardId: cardName,
            source: "persona",
            timestamp: new Date().toISOString(),
            content: state.contentCards[cardName],
          });
        }
      });
      return {
        ...state,
        transcript: [...state.transcript, ...tempArray],
        skipClicked: true,
      };
    }

    case "CLEAR_CONTENT_CARDS":
      return {
        ...state,
        contentCards: {},
        shownCardsThisTurn: [],
        contentCardThisturn: [],
        skipClicked: false,
      };
    case "ADD_ROTATION_CONTENT_CARDS":
      return {
        ...state,
        contentCardThisturn: action.payload.rotation,
      };
    case "DISCONNECT":
      state.scene?.disconnect();
      return {
        ...state,
        connected: false,
        videoPlaying: false,
        mute: false,
        chatOpen: false,
        transcript: [],
        contentCards: {},
        scene: null,
      };
    default:
      return state;
  }
};

export const StoreProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [redirectSurvey, setRedirectSurvey] = useState(false);

  const submisson = useCallback((mdEvent: any) => {
    window.location.replace(
      "https://www.nrma.com.au/car-insurance?intcmp=soul-machine-survey-ending"
    );
  }, []);
  useEffect(() => {
    if (redirectSurvey) {
      window.addEventListener("MDigital_Submit_Feedback", submisson);
      window.addEventListener("MDigital_Form_Close_No_Submit", submisson);
    }

    return () => {
      window.removeEventListener("MDigital_Submit_Feedback", submisson);
      window.removeEventListener("MDigital_Form_Close_No_Submit", submisson);
    };
  }, [submisson, redirectSurvey]);

  useEffect(() => {
    // WebSDK functions that need to be binded should be created here.
    // Setting Persona up
    if (state.scene) {
      state.scene.onMessage = (message) => {
        switch (message.name) {
          case "personaResponse":
            const { currentSpeech } = message.body;
            if (currentSpeech !== "") {
              dispatch({
                type: UserActionType.ADD_CONVERSATION_MESSAGE,
                payload: {
                  source: "persona",
                  text: currentSpeech,
                },
              });
              break;
            }
            return console.warn("ADD_PERSONA_MESSAGE: ignoring empty string");
          case "recognizeResults":
            const output = message.body.results[0];
            // sometimes we get an empty message, catch and log
            if (!output) {
              console.warn("undefined output!", message.body);
              return false;
            }
            const { transcript: text } = output.alternatives[0];
            // we get multiple recognizeResults messages, so only add the final one to transcript
            // but keep track of intermediate one to show the user what they're saying
            if (output.final === false) {
              return dispatch({
                type: UserActionType.SET_INTERMEDIATE_USER_UTTERANCE,
                payload: { text },
              });
            }
            return dispatch({
              type: UserActionType.ADD_CONVERSATION_MESSAGE,
              payload: {
                source: "user",
                text: text,
              },
            });
          // handle speech markers
          case "speechMarker": {
            const { name: speechMarkerName, arguments: args } = message.body;
            switch (speechMarkerName) {
              case "showcards": {
                const { contentCards } = state;
                // // if desired, multiple content cards can be displayed in one turn
                // // this will only ever be one card
                const newCards = args.map((contentCardName: string) => ({
                  id: contentCardName,
                  ...contentCards[contentCardName],
                }));

                dispatch({
                  type: UserActionType.ADD_CONVERSATION_MESSAGE,
                  payload: {
                    source: "persona",
                    text: newCards[0],
                  },
                });
                break;
              }
              case "marker": {
                // custom speech marker handler
                const { arguments: markerArgs } = message.body;
                const currentRotation: string[] = [];
                markerArgs.forEach((a: string) => {
                  switch (a) {
                    case "survey": {
                      setRedirectSurvey(true);
                      window.KAMPYLE_ONSITE_SDK.showForm(39838);

                      break;
                    }
                    default: {
                      // We need to add all item recieved here to our stop to show the content card
                      currentRotation.push(a);
                      // console.warn(`no handler for @marker(${a})!`);
                    }
                  }
                });
                dispatch({
                  type: UserActionType.ADD_ROTATION_CONTENT_CARDS,
                  payload: {
                    rotation: currentRotation,
                  },
                });
                break;
              }
              default: {
                console.warn(`unregonized speech marker: ${speechMarkerName}`);
              }
            }
            break;
          }
          // pull out content card data from contexts
          case "conversationResult": {
            // get content cards from context
            const { context } = message.body.output;
            // filter out $cardName.original, we just want values for $cardName
            const relevantKeys = Object.keys(context).filter(
              (k) => /\.original/gm.test(k) === false
            );
            let contentCards: {
              [key: string]:
                | HeadlineTypes
                | PlaintextTypes
                | CharacterSelectionTypes
                | BulletListTypes
                | TipsTypes
                | SliderTypes
                | ImgDescTypes
                | ImgTypes
                | ImgCtaButtonsTypes;
            } = {};
            relevantKeys.forEach((contentCardId: string) => {
              const cardKey = contentCardId.match(/public-(.*)/m)![1];
              // remove public- prefix from key
              try {
                contentCards[cardKey] = JSON.parse(context[contentCardId]);
              } catch {
                return console.error(
                  `invalid JSON in content card payload for ${contentCardId}!`
                );
              }
            });

            return dispatch({
              type: UserActionType.MODIFY_CONTENT_CARDS,
              payload: { contentCards },
            });
          }
          case "state": {
            const { body } = message;
            if ("persona" in body) {
              const personaState = body.persona[1];
              if ("speechState" in personaState) {
                const { speechState } = personaState;
                if (speechState === "idle") {
                  dispatch({
                    type: UserActionType.CLEAR_CONTENT_CARDS,
                  });
                }
              }
            }
            break;
          }

          default:
            break;
        }
      };
      state.scene.onDisconnected = () => {
        return dispatch({
          type: UserActionType.SET_ERROR,
          payload: { errorType: "timeout" },
        });
      };
    }
  }, [state.scene]);

  const value = { state, dispatch };
  return <Store.Provider value={value}>{children}</Store.Provider>;
};
